/**
 * Tiny shared store for per-item fulfill status, persisted to localStorage so it
 * survives navigation (the list page unmounts when you open an item) and lets
 * the detail-page actions (send PO / transfer / award) mark a row fulfilled.
 * Demo-only — not wired to any backend.
 */

import { useSyncExternalStore } from 'react';

export type FulfillStatus = 'open' | 'fulfilling' | 'fulfilled';

export type FulfillStatusMap = Record<string, FulfillStatus>;

/** Which option filled the item: a stock transfer or a purchase order. */
export type FulfillVia = 'transfer' | 'buy';

export type FulfillViaMap = Record<string, FulfillVia>;

const KEY = 'demo:fulfill-statuses';
const VIA_KEY = 'demo:fulfill-via';

function load<T extends Record<string, string>>(key: string, empty: T): T {
  if (typeof localStorage === 'undefined') return empty;
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : empty;
  } catch {
    // Corrupt/unavailable storage — start clean.
    return empty;
  }
}

let statuses: FulfillStatusMap = load<FulfillStatusMap>(KEY, {});
let vias: FulfillViaMap = load<FulfillViaMap>(VIA_KEY, {});
const listeners = new Set<() => void>();

function persist(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(KEY, JSON.stringify(statuses));
    localStorage.setItem(VIA_KEY, JSON.stringify(vias));
  } catch {
    // Private mode / quota — keep the in-memory value only.
  }
}

function emit(): void {
  for (const listener of listeners) listener();
}

function without(map: FulfillViaMap, id: string): FulfillViaMap {
  return Object.fromEntries(Object.entries(map).filter(([key]) => key !== id));
}

/**
 * Sets an item's status; `via` records which option filled it, so only that
 * option's button reads as sent. An item that is not fulfilled has no `via`.
 */
export function setFulfillStatus(
  id: string,
  status: FulfillStatus,
  via?: FulfillVia,
): void {
  const nextVia = status === 'fulfilled' ? (via ?? vias[id]) : undefined;
  if (statuses[id] === status && vias[id] === nextVia) return;
  statuses = { ...statuses, [id]: status };
  vias = nextVia === undefined ? without(vias, id) : { ...vias, [id]: nextVia };
  persist();
  emit();
}

export function getFulfillVia(id: string): FulfillVia | undefined {
  return vias[id];
}

/** Reactive: which option filled this item, once it is fulfilled. */
export function useFulfillVia(id: string | undefined): FulfillVia | undefined {
  return useSyncExternalStore(
    subscribe,
    () => (id === undefined ? undefined : vias[id]),
    () => (id === undefined ? undefined : vias[id]),
  );
}

/**
 * Whether a chat message is the "reset" command — case- and whitespace-
 * agnostic, so "Reset", " reset ", and "RE SET" all match.
 */
export function isResetCommand(text: string): boolean {
  return text.replace(/\s/g, '').toLowerCase() === 'reset';
}

/** Clear all statuses back to open — handy for re-running the demo. */
export function resetFulfillStatuses(): void {
  statuses = {};
  vias = {};
  persist();
  emit();
}

export function getFulfillStatus(id: string): FulfillStatus {
  return statuses[id] ?? 'open';
}

/**
 * Items whose other fill option (for example a transfer next to a purchase) has
 * been shown under the chart. Held in memory only, so a reload hides it again.
 */
let shownAlternatives: ReadonlySet<string> = new Set();
const alternativeListeners = new Set<() => void>();

function emitAlternatives(): void {
  for (const listener of alternativeListeners) listener();
}

export function showAlternative(id: string): void {
  if (shownAlternatives.has(id)) return;
  shownAlternatives = new Set([...shownAlternatives, id]);
  emitAlternatives();
}

export function hideAlternatives(): void {
  if (shownAlternatives.size === 0) return;
  shownAlternatives = new Set();
  emitAlternatives();
}

export function isAlternativeShown(id: string): boolean {
  return shownAlternatives.has(id);
}

function subscribeAlternatives(listener: () => void): () => void {
  alternativeListeners.add(listener);
  return () => {
    alternativeListeners.delete(listener);
  };
}

/** Reactive: whether the other fill option is showing for this item. */
export function useAlternativeShown(id: string): boolean {
  return useSyncExternalStore(
    subscribeAlternatives,
    () => shownAlternatives.has(id),
    () => shownAlternatives.has(id),
  );
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): FulfillStatusMap {
  return statuses;
}

/** Reactive view of the full status map. */
export function useFulfillStatuses(): FulfillStatusMap {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
